import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AdminAdmDefinitionService } from "../../../../api/services/admin-adm-definition.service";
import { AdminAdmGroupService } from "../../../../api/services/admin-adm-group.service";
import { AdminAdmTransferService } from "../../../../api/services/admin-adm-transfer.service";
import { AdmDefinitionResponse } from "../../../../api/models/adm-definition-response";
import { AdmGroupEx } from "../../../../api/models/adm-group-ex";
import { AdmTransferDocument } from "../../../../api/models/adm-transfer-document";

const MAX_FILE_BYTES = 20 * 1024 * 1024;
// Export and import of the Tables Definition. Export builds a name-based JSON file; import is a four-step flow -
// choose the file here, then the shared review component previews exactly what would happen (nothing is written),
// asks for acknowledgement of any guarded overwrites, and applies in one all-or-nothing transaction. Import only
// adds and updates; nothing is ever deleted.
@Component({
  selector: "admin-adm-transfer",
  templateUrl: "./admin-adm-transfer.component.html",
})
export class AdminAdmTransferComponent implements OnInit {
  // Raised after a successful import so the Definition editor reloads, and when the admin asks to see validation.
  @Output() imported = new EventEmitter<void>();
  @Output() openValidation = new EventEmitter<void>();

  public loading = true;
  public definition: AdmDefinitionResponse = { sources: [], tables: [], columns: [], fieldGroups: [], relationships: [], filterPoints: [], issues: [] };
  public groups: AdmGroupEx[] = [];

  // ---- export ----
  public tableSearch = "";
  public selectedTables = new Set<string>();
  public includeRelatedTables = false;
  public includeRetired = false;
  public includeGroups = false;
  public selectedGroups = new Set<string>();
  public exporting = false;
  public lastExport: AdmTransferDocument | null = null;

  // ---- import (the file step; the rest is the shared review component) ----
  public dragging = false;
  public fileName = "";
  public fileProblems: string[] = [];
  public document: AdmTransferDocument | null = null;
  public reviewing = false;

  constructor(
    private definitionService: AdminAdmDefinitionService,
    private groupService: AdminAdmGroupService,
    private transferService: AdminAdmTransferService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.loadLists();
  }

  loadLists() {
    this.loading = true;
    this.definitionService.adminAdmDefinitionGetDefinition().subscribe({
      next: (definition) => {
        this.definition = definition;
        this.groupService.adminAdmGroupGetGroups().subscribe({
          next: (groups) => { this.groups = groups || []; this.loading = false; },
          error: () => { this.loading = false; },
        });
      },
      error: () => {
        this.loading = false;
        this.snackBar.open("Could not load the Tables Definition", null, { duration: 5000 });
      },
    });
  }

  // =============================== export =====================================================================
  get tables() { return (this.definition.tables || []).slice().sort((a, b) => a.tableName.localeCompare(b.tableName)); }

  get visibleTables() {
    const q = this.tableSearch.trim().toLowerCase();
    return this.tables.filter((t) => !q || t.tableName.toLowerCase().includes(q) || (t.displayName || "").toLowerCase().includes(q));
  }

  columnCount(tableId: number): number {
    return (this.definition.columns || []).filter((c) => c.admTableId === tableId && (this.includeRetired || !c.retired)).length;
  }

  toggleTable(name: string, on: boolean) { on ? this.selectedTables.add(name) : this.selectedTables.delete(name); }
  selectVisible() { this.visibleTables.forEach((t) => this.selectedTables.add(t.tableName)); }
  clearTables() { this.selectedTables.clear(); }
  toggleGroup(name: string, on: boolean) { on ? this.selectedGroups.add(name) : this.selectedGroups.delete(name); }

  get exportScope(): string {
    return this.selectedTables.size === 0 ? "all tables" : `${this.selectedTables.size} selected table${this.selectedTables.size === 1 ? "" : "s"}`;
  }

  export() {
    this.exporting = true;
    this.lastExport = null;
    this.transferService
      .adminAdmTransferExport({
        tables: Array.from(this.selectedTables),
        includeRelatedTables: this.includeRelatedTables,
        includeRetired: this.includeRetired,
        includeGroups: this.includeGroups,
        groups: this.includeGroups ? Array.from(this.selectedGroups) : [],
      })
      .subscribe({
        next: (doc) => {
          this.exporting = false;
          this.lastExport = doc;
          const env = (doc.exportedFromEnvironment || "env").toLowerCase().replace(/[^a-z0-9]+/g, "-");
          this.download(doc, `adm-definition-${env}-${new Date().toISOString().substring(0, 10)}.json`);
        },
        error: (e) => {
          this.exporting = false;
          this.snackBar.open(this.errorText(e, "Could not build the export"), "Close", { duration: 10000 });
        },
      });
  }

  exportSummary(doc: AdmTransferDocument): string {
    const columns = (doc.tables || []).reduce((n, t) => n + (t.columns || []).filter((c) => c.kind !== "Flattened").length, 0);
    const flattened = (doc.tables || []).reduce((n, t) => n + (t.columns || []).filter((c) => c.kind === "Flattened").length, 0);
    const parts = [
      `${(doc.tables || []).length} tables`,
      `${columns} columns`,
      `${flattened} flattened columns`,
      `${(doc.relationships || []).length} relationships`,
      `${(doc.filterPoints || []).length} filter points`,
    ];
    if ((doc.groups || []).length > 0) parts.push(`${doc.groups.length} Groups`);
    return parts.join(", ");
  }

  private download(content: unknown, fileName: string) {
    const blob = new Blob([JSON.stringify(content, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // =============================== import: step 1, the file ===================================================
  onFileInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) this.readFile(input.files[0]);
    input.value = "";
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;
    if (event.dataTransfer && event.dataTransfer.files.length > 0) this.readFile(event.dataTransfer.files[0]);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragging = true;
  }

  private readFile(file: File) {
    this.resetImport();
    this.fileName = file.name;
    if (file.size > MAX_FILE_BYTES) { this.fileProblems = [`The file is ${(file.size / 1048576).toFixed(1)} MB; the limit is 20 MB.`]; return; }
    if (!/\.json$/i.test(file.name)) { this.fileProblems = ["Choose a .json file exported from this page."]; return; }

    const reader = new FileReader();
    reader.onerror = () => { this.fileProblems = ["The file could not be read."]; };
    reader.onload = () => {
      let parsed: any;
      try { parsed = JSON.parse(String(reader.result)); }
      catch { this.fileProblems = ["The file is not valid JSON."]; return; }

      const problems: string[] = [];
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) problems.push("The file does not look like an ADM definition export.");
      else {
        if (parsed.formatVersion !== 1) problems.push(`Unsupported format version${parsed.formatVersion === undefined ? " (missing)" : ` ${parsed.formatVersion}`}; this page reads version 1.`);
        for (const key of ["tables", "relationships", "filterPoints"]) {
          if (parsed[key] !== undefined && !Array.isArray(parsed[key])) problems.push(`"${key}" must be a list.`);
        }
        if (!Array.isArray(parsed.tables) && !Array.isArray(parsed.relationships) && !Array.isArray(parsed.filterPoints) && !Array.isArray(parsed.groups))
          problems.push("The file contains no tables, relationships, filter points or Groups.");
      }
      this.fileProblems = problems;
      if (problems.length === 0) this.document = parsed as AdmTransferDocument;
    };
    reader.readAsText(file);
  }

  get fileSummary(): string { return this.document ? this.exportSummary(this.document) : ""; }

  // =============================== import: hand over to the review ============================================
  startReview() {
    if (this.document && this.fileProblems.length === 0) this.reviewing = true;
  }

  onImported() {
    this.imported.emit();
    this.loadLists();
  }

  resetImport() {
    this.reviewing = false;
    this.fileName = "";
    this.fileProblems = [];
    this.document = null;
  }

  private errorText(e: any, fallback: string): string {
    const body = e && e.error;
    if (typeof body === "string" && body.length > 0) return body;
    if (body && (body.title || body.message)) return body.title || body.message;
    return fallback;
  }
}
